import { useState } from 'react';

import { useTranslations } from 'next-intl';

import { CHAT_ASSISTANT_LIMITS, type ChatAttachment } from '@repo/contracts';
import { useNotification } from '@repo/ui/hooks/useNotification';

const MAX_FILE_COUNT = CHAT_ASSISTANT_LIMITS.maxAttachmentsPerMessage;
const MAX_FILE_SIZE_MB = 5;
const MAX_TOTAL_FILE_SIZE_MB = 15;

export function useFileProcessor(
    attachments: ChatAttachment[] | undefined,
    setAttachments: ((atts: ChatAttachment[]) => void) | undefined,
) {
    const t = useTranslations();
    const notification = useNotification();

    const processFiles = async (files: File[]) => {
        if (!files || files.length === 0) return;

        const currentAttachments = attachments || [];
        const currentCount = currentAttachments.length;
        
        if (currentCount + files.length > MAX_FILE_COUNT) {
            notification('error', t('file-count-limit-exceeded', { max: MAX_FILE_COUNT }));
            return;
        }

        const currentTotalSize = currentAttachments.reduce((acc, att) => acc + (att.data.length * 3) / 4, 0);
        const newTotalSize = files.reduce((acc, f) => acc + f.size, 0);

        if (currentTotalSize + newTotalSize > MAX_TOTAL_FILE_SIZE_MB * 1024 * 1024) {
            notification('error', t('file-total-size-limit-exceeded', { maxMB: MAX_TOTAL_FILE_SIZE_MB }));
            return;
        }

        const invalidTypeFile = files.find((f) => 
            f.type !== 'application/pdf' && 
            f.type !== 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' &&
            !f.type.startsWith('text/') && 
            !f.type.startsWith('image/'),
        );
        if (invalidTypeFile) {
            notification('error', t('file-type-not-supported'));
            return;
        }

        const oversizedFile = files.find((f) => f.size > MAX_FILE_SIZE_MB * 1024 * 1024);
        if (oversizedFile) {
            notification('error', t('file-size-limit-exceeded', { maxMB: MAX_FILE_SIZE_MB }));
            return;
        }

        if (!setAttachments) return;

        const readPromises = files.map((file) => {
            return new Promise<ChatAttachment | null>((resolve) => {
                const reader = new FileReader();
                reader.onload = (event) => {
                    const base64Data = (event.target?.result as string).split(',')[1];
                    if (base64Data) {
                        resolve({
                            name: file.name,
                            type: file.type,
                            data: base64Data,
                        });
                    } else {
                        resolve(null);
                    }
                };
                reader.onerror = () => resolve(null);
                reader.readAsDataURL(file);
            });
        });

        const newAttachments = (await Promise.all(readPromises)).filter((a) => a !== null) as ChatAttachment[];
        setAttachments([...currentAttachments, ...newAttachments]);
    };

    const [isDragging, setIsDragging] = useState(false);

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDrop = async (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            await processFiles(Array.from(e.dataTransfer.files));
        }
    };

    return { 
        processFiles,
        isDragging,
        dragEvents: {
            onDragOver: handleDragOver,
            onDragLeave: handleDragLeave,
            onDrop: handleDrop,
        },
    };
}
